import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Button, ListGroup, Badge, Modal, Image, Form, Spinner, Card, Row, Col, Alert } from 'react-bootstrap';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import SiteNavbar from './SiteNavbar';
import SiteFooter from './SiteFooter';

const ALLOWED_SHIPPING = [
  { company: 'Correios', service: 'PAC' },
  { company: 'Correios', service: 'SEDEX' },
  { company: 'Jadlog',   service: '.pacote' },
  { company: 'Jadlog',   service: '.com' },
  { company: 'Loggi',    service: 'Express' },
  { company: 'Loggi',    service: 'Loggi Ponto' },
];

const isAllowedService = (opt) => ALLOWED_SHIPPING.some(a => {
  const company     = (opt.company?.name || opt.carrier || '').toLowerCase();
  const serviceName = (opt.name || opt.service_description || '').toLowerCase();
  return company.includes(a.company.toLowerCase()) && serviceName.includes(a.service.toLowerCase());
});

// CEP e frete escolhidos pelo visitante, guardados para retomar o checkout após login/cadastro
const GUEST_SHIPPING_KEY = 'checkoutGuestShipping';

const readGuestShipping = () => {
  try { return JSON.parse(sessionStorage.getItem(GUEST_SHIPPING_KEY)) || {}; }
  catch { return {}; }
};

const shippingOptionKey = (opt) =>
  String(opt?.id ?? `${opt?.company?.name || opt?.carrier || ''}|${opt?.name || opt?.service_description || ''}`);

const Checkout = () => {
  const { cart, clearCart, removeFromCart, updateQuantity } = useCart();
  const isLoggedIn = !!sessionStorage.getItem('customerToken');
  const totalPrice = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const navigate = useNavigate();
  const cartRef = useRef(cart);
  useEffect(() => { cartRef.current = cart; }, [cart]);

  const [showPixModal, setShowPixModal] = useState(false);
  const [pixQrImage, setPixQrImage]     = useState(null);
  const [pixCode, setPixCode]           = useState(null);
  const [pixPaymentId, setPixPaymentId] = useState(null);
  const [pixStatus, setPixStatus]       = useState('pending'); // 'pending' | 'approved'

  const [customer, setCustomer] = useState({ name: '', email: '', phone: '', cpf: '' });
  const [delivery, setDelivery] = useState({
    postalCode: '', street: '', number: '', complement: '', district: '', city: '', state: '',
  });
  const deliveryRef = useRef(null);
  useEffect(() => { deliveryRef.current = delivery; }, [delivery]);

  const [profileLoading, setProfileLoading]   = useState(true);
  const [profileLoaded, setProfileLoaded]     = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [cepLoading, setCepLoading]           = useState(false);

  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedOption, setSelectedOption]   = useState(null);
  const [shippingError, setShippingError]     = useState('');

  // ── Cotação de frete (aceita CEP direto para chamada automática) ─────────────
  const quoteShipping = async (cepOverride) => {
    const currentCart = cartRef.current;
    if (!currentCart.length) return;
    const cep = (cepOverride || delivery.postalCode).replace(/\D/g, '');
    if (cep.length !== 8) return;

    setShippingLoading(true);
    setShippingOptions([]);
    setSelectedOption(null);
    setShippingError('');

    try {
      // Busca dimensões frescas da API para evitar dados desatualizados do localStorage
      let freshDimensions = {};
      try {
        const res = await api.get('/products');
        const list = Array.isArray(res.data) ? res.data : (res.data?.products || []);
        list.forEach(p => { freshDimensions[p._id] = p; });
      } catch (_) { /* usa dados do carrinho como fallback */ }

      const supplierCep = (currentCart[0]?.supplier?.address?.cep || '12235000').replace(/\D/g, '');
      const from  = { zipcode: supplierCep };
      const to    = { zipcode: cep };
      const items = currentCart.map(item => {
        const fresh = freshDimensions[item._id] || item;
        return {
          amount: parseInt(item.quantity, 10) || 1,
          weight: parseFloat(fresh.weight || 0) || 0.1,
          height: parseFloat(fresh.dimensions?.height || 1) || 1,
          width:  parseFloat(fresh.dimensions?.width  || 1) || 1,
          length: parseFloat(fresh.dimensions?.length || 1) || 1,
        };
      });

      const response = await api.post('/shipping/quote', { from, to, items });
      const data = response.data;
      if (!data) { setShippingError('Sem opções de frete disponíveis.'); return; }

      let options = [];
      if (Array.isArray(data))                                options = data;
      else if (data.fallback && Array.isArray(data.options))  options = data.options;
      else if (Array.isArray(data.options))                   options = data.options;
      else if (Array.isArray(data.results))                   options = data.results;
      else if (Array.isArray(data.services))                  options = data.services;
      else if (Array.isArray(data.items))                     options = data.items;

      const valid    = options.filter(o => !o.error);
      const filtered = valid.filter(isAllowedService);
      const finalOptions = filtered.length > 0 ? filtered : valid;
      setShippingOptions(finalOptions);

      // Reaproveita o frete escolhido antes do login, se o CEP for o mesmo
      const saved = readGuestShipping();
      if (saved.cep === cep && saved.optionKey) {
        const match = finalOptions.find(o => shippingOptionKey(o) === saved.optionKey);
        if (match) setSelectedOption(match);
      }
    } catch (error) {
      const errData = error.response?.data?.error;
      const msg = typeof errData === 'object' ? JSON.stringify(errData) : (errData || error.message || 'Erro desconhecido');
      setShippingError('Erro ao cotar frete: ' + msg);
    } finally {
      setShippingLoading(false);
    }
  };

  // Re-cotar frete quando quantidade dos itens muda (efeito roda após cartRef ser atualizado)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    const cep = (deliveryRef.current?.postalCode || '').replace(/\D/g, '');
    if (cep.length === 8) quoteShipping(cep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  // ── Carregar perfil do cliente logado e cotar frete automaticamente ──────────
  useEffect(() => {
    const savedCep = (readGuestShipping().cep || '').replace(/\D/g, '');
    const token = sessionStorage.getItem('customerToken');
    if (!token) {
      // Visitante: só o CEP é pedido para calcular o frete
      setProfileLoading(false);
      if (savedCep.length === 8) {
        setDelivery(d => ({ ...d, postalCode: savedCep }));
        quoteShipping(savedCep);
      }
      return;
    }

    api.get('/auth/me')
      .then(async ({ data }) => {
        // Usa o endereço de entrega alternativo quando o cliente cadastrou um; senão, o residencial.
        const hasDelivery = ['cep', 'street', 'number', 'city'].some(
          f => String(data.deliveryAddress?.[f] || '').trim() !== ''
        );
        const src = hasDelivery ? data.deliveryAddress : (data.address || {});
        const cep = (src.cep || savedCep).replace(/\D/g, '');
        const addr = {
          postalCode: src.cep          || savedCep,
          street:     src.street       || '',
          number:     src.number       || '',
          complement: src.complement   || '',
          district:   src.neighborhood || '',
          city:       src.city         || '',
          state:      src.state        || '',
        };
        const cust = {
          name:  data.name  || '',
          email: data.email || '',
          phone: data.phone || '',
          cpf:   data.cpf   || '',
        };
        setCustomer(cust);
        setDelivery(addr);
        setProfileLoaded(true);

        const addressComplete = cep.length === 8 && addr.street && addr.number && addr.city;
        if (!addressComplete || !cust.cpf) {
          setShowAddressForm(true);
          if (addressComplete) await quoteShipping(cep);
        } else {
          // Cotar frete automaticamente com o CEP do cadastro
          await quoteShipping(cep);
        }
      })
      .catch(() => {
        setShowAddressForm(true);
      })
      .finally(() => setProfileLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── ViaCEP ──────────────────────────────────────────────────────────────────
  const handleCepBlur = async () => {
    const cep = delivery.postalCode.replace(/\D/g, '');
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const res  = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) { alert('CEP não encontrado.'); return; }
      setDelivery(d => ({
        ...d,
        street:   data.logradouro || '',
        district: data.bairro     || '',
        city:     data.localidade || '',
        state:    data.uf         || '',
      }));
    } catch { alert('Erro ao buscar CEP.'); }
    finally { setCepLoading(false); }
  };

  const selectedShippingPrice     = selectedOption
    ? parseFloat(selectedOption.price ?? selectedOption.total_price ?? selectedOption.value ?? 0) || 0
    : 0;
  const totalWithShipping         = totalPrice + selectedShippingPrice;
  const shippingSelectionRequired = shippingOptions.length > 0 && !selectedOption;
  const customerValid = customer.name.trim() && customer.email.trim() && customer.phone.trim() && customer.cpf.replace(/\D/g, '').length === 11;
  const addressValid  = delivery.postalCode && delivery.street && delivery.number && delivery.city;
  const canPay = cart.length > 0 && !shippingSelectionRequired && customerValid && addressValid && shippingOptions.length > 0;

  // ── Visitante segue para o pagamento: exige login/cadastro e volta ao checkout ──
  const handleGuestContinue = () => {
    sessionStorage.setItem(GUEST_SHIPPING_KEY, JSON.stringify({
      cep: delivery.postalCode.replace(/\D/g, ''),
      optionKey: shippingOptionKey(selectedOption),
    }));
    navigate('/login', { state: { from: '/checkout', reason: 'checkout' } });
  };

  // ── Pagamento cartão/boleto ──────────────────────────────────────────────────
  const handlePayment = async () => {
    if (!canPay) return;
    const paymentWindow = window.open('about:blank', '_blank');
    try {
      const items = cart.map(i => ({ title: i.name, price: i.price, quantity: i.quantity }));
      const response = await api.post('/payment', {
        items, shippingOption: selectedOption, shippingTotal: Number(selectedShippingPrice || 0),
        customer, delivery: { ...delivery, postalCode: delivery.postalCode.replace(/\D/g, '') },
        fromAddress: cart[0]?.supplier || null,
      });
      const { paymentUrl, init_point, sandbox_init_point } = response.data || {};
      const finalUrl = paymentUrl || init_point || sandbox_init_point;
      if (!finalUrl) { paymentWindow?.close(); throw new Error('Falha ao obter link de pagamento.'); }
      paymentWindow ? (paymentWindow.location.href = finalUrl) : (window.location.href = finalUrl);
    } catch (error) {
      paymentWindow?.close();
      alert(`Erro ao processar o pagamento:\n${error.response?.data?.error || error.message}`);
    }
  };

  // ── PIX ─────────────────────────────────────────────────────────────────────
  const handlePixPayment = async () => {
    if (!canPay) return;
    try {
      const items = cart.map(i => ({ title: i.name, price: i.price, quantity: i.quantity }));
      const response = await api.post('/payment/pix', {
        items, shippingOption: selectedOption, shippingTotal: Number(selectedShippingPrice || 0),
        payer: { email: customer.email, name: customer.name },
        customer, delivery: { ...delivery, postalCode: delivery.postalCode.replace(/\D/g, '') },
        fromAddress: cart[0]?.supplier || null,
      });
      const data = response.data || {};
      const tx   = data.point_of_interaction?.transaction_data || {};
      if (!tx.qr_code && !tx.qr_code_base64) throw new Error('O Mercado Pago não retornou o QR Code do PIX.');
      setPixQrImage(tx.qr_code_base64 ? `data:image/png;base64,${tx.qr_code_base64}` : null);
      setPixCode(tx.qr_code || null);
      setPixPaymentId(data.id || null);
      setPixStatus('pending');
      setShowPixModal(true);
    } catch (error) {
      alert(`Erro ao processar pagamento PIX:\n${error.response?.data?.error || error.message}`);
    }
  };

  // ── Verifica automaticamente quando o PIX é pago ─────────────────────────────
  useEffect(() => {
    if (!showPixModal || !pixPaymentId || pixStatus === 'approved') return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/payment/status/${pixPaymentId}`);
        if (data.status === 'approved') {
          setPixStatus('approved');
          clearInterval(interval);
        }
      } catch (_) { /* silencioso: continua tentando */ }
    }, 4000);
    return () => clearInterval(interval);
  }, [showPixModal, pixPaymentId, pixStatus]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      <SiteNavbar />
      <Container className="mt-4" style={{ maxWidth: 640 }}>
        <h2 className="mb-4">Finalizar Compra</h2>

        {cart.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">Seu carrinho está vazio.</p>
            <Button variant="warning" onClick={() => navigate('/')}>Continuar Comprando</Button>
          </div>
        ) : (
          <>
            {/* ── Itens ── */}
            <ListGroup variant="flush" className="mb-4">
              {cart.map(item => (
                <ListGroup.Item key={item._id} className="px-0">
                  <div className="d-flex align-items-center gap-2">
                    {item.images?.[0] && (
                      <img src={item.images[0]} alt={item.name}
                        style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                    )}
                    <div className="flex-grow-1">
                      <div className="fw-semibold" style={{ fontSize: '0.95rem' }}>{item.name}</div>
                      <small className="text-muted">R$ {item.price.toFixed(2).replace('.', ',')} cada</small>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <Button variant="outline-secondary" size="sm" onClick={() => updateQuantity(item._id, -1)}>−</Button>
                      <Badge bg="secondary" style={{ fontSize: '0.9rem', minWidth: 28 }}>{item.quantity}</Badge>
                      <Button variant="outline-secondary" size="sm" onClick={() => updateQuantity(item._id, 1)}>+</Button>
                    </div>
                    <div className="text-end ms-2" style={{ minWidth: 80 }}>
                      <div className="fw-bold">R$ {(item.price * item.quantity).toFixed(2).replace('.', ',')}</div>
                      <Button variant="link" size="sm" className="text-danger p-0" onClick={() => removeFromCart(item._id)}>
                        Remover
                      </Button>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>

            {/* ── Totais ── */}
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span>Subtotal</span>
                <span>R$ {totalPrice.toFixed(2).replace('.', ',')}</span>
              </div>
              {selectedShippingPrice > 0 && (
                <div className="d-flex justify-content-between mb-1">
                  <span>Frete ({selectedOption?.name || selectedOption?.service_description || ''})</span>
                  <span>R$ {selectedShippingPrice.toFixed(2).replace('.', ',')}</span>
                </div>
              )}
              <div className="d-flex justify-content-between fs-5 fw-bold mt-2">
                <span>Total</span>
                <span>R$ {totalWithShipping.toFixed(2).replace('.', ',')}</span>
              </div>
            </div>

            {/* ── Carregando perfil ── */}
            {profileLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                <span className="text-muted">Carregando seus dados...</span>
              </div>
            )}

            {!profileLoading && (
              <>
                {/* ── Visitante: apenas CEP para calcular o frete ── */}
                {!isLoggedIn && (
                  <Card className="mb-3">
                    <Card.Body>
                      <h5 className="mb-3">Calcular frete</h5>
                      <Form onSubmit={e => { e.preventDefault(); quoteShipping(); }}>
                        <Row className="g-2 align-items-center">
                          <Col xs={7} sm={5}>
                            <Form.Control placeholder="Digite seu CEP" value={delivery.postalCode}
                              onChange={e => setDelivery(d => ({ ...d, postalCode: e.target.value }))}
                              maxLength={9} inputMode="numeric" />
                          </Col>
                          <Col xs="auto">
                            <Button type="submit"
                              disabled={shippingLoading || delivery.postalCode.replace(/\D/g, '').length !== 8}>
                              {shippingLoading
                                ? <><Spinner animation="border" size="sm" /> Cotando...</>
                                : 'Calcular Frete'}
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    </Card.Body>
                  </Card>
                )}

                {/* ── Resumo do cliente (logado com dados completos) ── */}
                {profileLoaded && !showAddressForm && (
                  <Card className="mb-3 border-success">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-bold">{customer.name}</div>
                          <div className="text-muted small">{customer.email} · {customer.phone}</div>
                          <div className="mt-1 small">
                            {delivery.street}, {delivery.number}
                            {delivery.complement ? ` — ${delivery.complement}` : ''}<br />
                            {delivery.district} · {delivery.city}/{delivery.state} · CEP {delivery.postalCode}
                          </div>
                        </div>
                        <Button variant="outline-secondary" size="sm"
                          onClick={() => setShowAddressForm(true)}>
                          Alterar
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* ── Formulário (não logado ou endereço incompleto ou editando) ── */}
                {showAddressForm && (
                  <>
                    <Card className="mb-3">
                      <Card.Body>
                        <h5 className="mb-3">Seus dados</h5>
                        <Row className="g-2">
                          <Col xs={12}>
                            <Form.Control placeholder="Nome completo *" value={customer.name}
                              onChange={e => setCustomer(c => ({ ...c, name: e.target.value }))} />
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Control type="email" placeholder="E-mail *" value={customer.email}
                              onChange={e => setCustomer(c => ({ ...c, email: e.target.value }))} />
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Control placeholder="Telefone/WhatsApp *" value={customer.phone}
                              onChange={e => setCustomer(c => ({ ...c, phone: e.target.value }))} />
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Control placeholder="CPF *" value={customer.cpf}
                              onChange={e => setCustomer(c => ({ ...c, cpf: e.target.value }))}
                              maxLength={14} />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    <Card className="mb-3">
                      <Card.Body>
                        <h5 className="mb-3">Endereço de entrega</h5>
                        <Row className="g-2">
                          <Col xs={12} sm={5}>
                            <Form.Control placeholder="CEP *" value={delivery.postalCode}
                              onChange={e => setDelivery(d => ({ ...d, postalCode: e.target.value }))}
                              onBlur={handleCepBlur} maxLength={9} />
                            {cepLoading && <small className="text-muted">Buscando CEP...</small>}
                          </Col>
                          <Col xs={12} sm={7}>
                            <Form.Control placeholder="Rua / Logradouro" value={delivery.street}
                              onChange={e => setDelivery(d => ({ ...d, street: e.target.value }))} />
                          </Col>
                          <Col xs={6} sm={4}>
                            <Form.Control placeholder="Número *" value={delivery.number}
                              onChange={e => setDelivery(d => ({ ...d, number: e.target.value }))} />
                          </Col>
                          <Col xs={6} sm={8}>
                            <Form.Control placeholder="Complemento" value={delivery.complement}
                              onChange={e => setDelivery(d => ({ ...d, complement: e.target.value }))} />
                          </Col>
                          <Col xs={12} sm={5}>
                            <Form.Control placeholder="Bairro" value={delivery.district}
                              onChange={e => setDelivery(d => ({ ...d, district: e.target.value }))} />
                          </Col>
                          <Col xs={8} sm={5}>
                            <Form.Control placeholder="Cidade" value={delivery.city}
                              onChange={e => setDelivery(d => ({ ...d, city: e.target.value }))} />
                          </Col>
                          <Col xs={4} sm={2}>
                            <Form.Control placeholder="UF" value={delivery.state} maxLength={2}
                              onChange={e => setDelivery(d => ({ ...d, state: e.target.value.toUpperCase() }))} />
                          </Col>
                        </Row>

                        <div className="mt-3 d-flex gap-2 flex-wrap">
                          <Button onClick={() => quoteShipping()} disabled={shippingLoading || !delivery.postalCode}>
                            {shippingLoading
                              ? <><Spinner animation="border" size="sm" /> Cotando...</>
                              : 'Calcular Frete'}
                          </Button>
                          {profileLoaded && (
                            <Button variant="outline-secondary"
                              onClick={() => { setShowAddressForm(false); quoteShipping(delivery.postalCode); }}>
                              Cancelar
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </>
                )}

                {/* ── Cotando frete automaticamente ── */}
                {shippingLoading && isLoggedIn && !showAddressForm && (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span className="text-muted">Calculando frete para o seu endereço...</span>
                  </div>
                )}

                {/* ── Erro de frete ── */}
                {shippingError && (
                  <Alert variant="warning" className="mb-3">
                    {shippingError}
                    <Button variant="link" size="sm" className="p-0 ms-2"
                      onClick={() => quoteShipping()}>
                      Tentar novamente
                    </Button>
                  </Alert>
                )}

                {/* ── Opções de frete ── */}
                {shippingOptions.length > 0 && (
                  <Card className="mb-3">
                    <Card.Body>
                      <h6 className="mb-3">Escolha o frete</h6>
                      {shippingOptions.map((opt, idx) => (
                        <div key={idx}
                          onClick={() => setSelectedOption(opt)}
                          style={{ cursor: 'pointer' }}
                          className={`d-flex align-items-center justify-content-between p-2 border rounded mb-2 ${selectedOption === opt ? 'border-success bg-light' : ''}`}>
                          <div>
                            <div className="fw-semibold">
                              {opt.company?.name ? `${opt.company.name} — ` : ''}
                              {opt.name || opt.service_description || opt.carrier || `Opção ${idx + 1}`}
                            </div>
                            <div className="text-muted small">
                              Prazo: {opt.delivery_time || opt.estimated_delivery_time || opt.days || '—'} dias úteis
                            </div>
                          </div>
                          <div className="text-end ms-3">
                            <div className="fw-bold">
                              R$ {parseFloat(opt.price || opt.total_price || 0).toFixed(2).replace('.', ',')}
                            </div>
                            {selectedOption === opt && (
                              <span className="text-success small">✓ Selecionado</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                )}

                {/* ── Botões de pagamento ── */}
                {shippingSelectionRequired && (
                  <p className="text-danger mb-2">Selecione uma opção de frete para continuar.</p>
                )}
                {isLoggedIn && !customerValid && !profileLoading && (
                  <p className="text-warning mb-2">Preencha seus dados pessoais para continuar.</p>
                )}

                <div className="d-grid gap-2 mb-4">
                  {isLoggedIn ? (
                    <>
                      <Button variant="warning" size="lg" onClick={handlePayment} disabled={!canPay}>
                        Pagar com cartões e boletos
                      </Button>
                      <Button variant="success" size="lg" onClick={handlePixPayment} disabled={!canPay}>
                        Pagar com PIX
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="warning" size="lg" onClick={handleGuestContinue}
                        disabled={!selectedOption}>
                        Continuar para o pagamento
                      </Button>
                      {selectedOption && (
                        <small className="text-muted text-center">
                          Para pagar, entre na sua conta ou faça seu cadastro.
                        </small>
                      )}
                    </>
                  )}
                  <Button variant="outline-secondary" onClick={() => navigate('/')}>Continuar Comprando</Button>
                  <Button variant="outline-danger" size="sm" onClick={clearCart}>Esvaziar Carrinho</Button>
                </div>
              </>
            )}
          </>
        )}
      </Container>
      <SiteFooter />

      <Modal show={showPixModal} onHide={() => setShowPixModal(false)} centered
        backdrop={pixStatus === 'approved' ? 'static' : true}>
        <Modal.Header closeButton={pixStatus !== 'approved'}>
          <Modal.Title>{pixStatus === 'approved' ? 'Pagamento confirmado' : 'Pagamento via PIX'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {pixStatus === 'approved' ? (
            <>
              <div style={{ fontSize: '4rem', lineHeight: 1 }}>✅</div>
              <h5 className="mt-3 mb-1 text-success">Pagamento aprovado!</h5>
              <p className="text-muted mb-0">
                Recebemos seu pagamento. Você receberá a confirmação e os detalhes do envio por e-mail.
              </p>
            </>
          ) : (
            <>
              {pixQrImage
                ? <Image src={pixQrImage} alt="PIX QR" fluid />
                : <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{pixCode}</pre>}
              <p className="mt-3 mb-2">Abra seu app bancário e aponte para o QR ou copie o código.</p>
              {pixCode && (
                <Button variant="outline-secondary" size="sm" className="mb-3"
                  onClick={() => { navigator.clipboard?.writeText(pixCode); }}>
                  Copiar código PIX
                </Button>
              )}
              <div className="d-flex align-items-center justify-content-center gap-2 text-muted">
                <Spinner animation="border" size="sm" />
                <span>Aguardando confirmação do pagamento…</span>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {pixStatus === 'approved' ? (
            <Button variant="success" onClick={() => { setShowPixModal(false); navigate('/pedido-confirmado'); }}>
              Concluir
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setShowPixModal(false)}>Fechar</Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Checkout;
